import React, { useCallback, useEffect, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import CentralCalls from '../../centralCalls';
import { is } from 'date-fns/locale';

const CrewSelector = React.memo(function CrewSelector({ onSave }) {
  const getOptionLabel = useCallback((option) => option.name || '', []);
  const isOptionEqualToValue = useCallback((o, v) => o.oid === v.oid, []);

  const [employees, setEmployees] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [teamName, setTeamName] = useState('');
  const [hexColorCode, setHexColorCode] = useState('#debfa6');
  const [inactive, setInactive] = useState(0); // 0 = active, 1 = inactive
  const [isPrimary, setIsPrimary] = useState(0);

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const people = await CentralCalls.fetchTableData('employee', { inactive: 0 });
        setEmployees(people);
      } catch (error) {
        console.error('Failed to fetch employees:', error);
        setEmployees([]);
      }
    };
    fetchEmployees();
  }, []);
  const checkForDuplicateCrew = async (crewName, employees) => {
    try {
      const employeeOids = employees.map(emp => emp.oid);
      const result = await CentralCalls.callStoredProcedureWithParams('sp_checkExistingCrew', { teamName: crewName, employeeOids: employeeOids }); 
      return result && result.length > 0;
    } catch (error) {
      console.error('Error checking for duplicate crew:', error);
      return true;
    }
  };

  const handleSubmit = async () => {
    if (!teamName.trim()) {
      alert('Team name is required.');
      return;
    }
    
    const isDuplicate = await checkForDuplicateCrew(teamName, selectedEmployees);
    if (isDuplicate) {
      alert('A crew with this name and/or employee combination already exists');
      return;
    }
    await CentralCalls.upsertData({
      employee_oids: JSON.stringify(selectedEmployees.map(emp => emp.oid)),
      name: teamName,
      hex_color_code: hexColorCode,
      inactive: inactive,
      is_primary: isPrimary,
    }, 'employee_team', 'insert');

    if (onSave) onSave();
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', maxWidth: '400px' }}>
      <Autocomplete
        multiple
        options={employees}
        value={selectedEmployees}
        onChange={(e, newVal) => setSelectedEmployees(newVal)}
        getOptionLabel={getOptionLabel}
        isOptionEqualToValue={isOptionEqualToValue}
        disableCloseOnSelect
        filterOptions={(options, state) =>
          options
            .filter((o) =>
              o.name?.toLowerCase().includes(state.inputValue.toLowerCase())
            )
            .slice(0, 50)
        }
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <span key={option.oid} {...getTagProps({ index })}>
              {option.name}
            </span>
          ))
        }
        renderInput={(params) => (
          <TextField {...params} label="Select Employees" variant="standard" />
        )}
      />

      <TextField
        label="Team Name"
        variant="standard"
        inputProps={{ maxLength: 50 }}
        value={teamName}
        onChange={(e) => setTeamName(e.target.value)}
      />

      <label>
        Team Color:
        <input
          type="color"
          value={hexColorCode}
          onChange={(e) => setHexColorCode(e.target.value)}
          style={{ marginLeft: '10px', verticalAlign: 'middle' }}
        />
        <span style={{ marginLeft: '10px' }}>{hexColorCode}</span>
      </label>

      <FormControlLabel
        control={
          <Switch
            checked={inactive === 1}
            onChange={(e) => setInactive(e.target.checked ? 1 : 0)}
            color="primary"
          />
        }
        label="Inactive"
      />

      <FormControlLabel
        control={
          <Switch
            checked={isPrimary === 1}
            onChange={(e) => setIsPrimary(e.target.checked ? 1 : 0)}
            color="primary"
          />
        }
        label="Primary"
      />

      <Button variant="contained" color="primary" onClick={handleSubmit}>
        Save Team
      </Button>
    </div>
  );
});

export default CrewSelector;
